import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Stack,
  IconButton,
  Button,
  Divider,
  styled,
  Grid,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import SingleTicketCard from "./SingleTicketCard";
import { useFormik } from "formik";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import SideNavbar from "../../../components/sideNavbar";
import TicketComment from "./TicketComment";
import axiosInstance from "../../../services/axiosInstance";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  MessageSquare,
  ChevronRight,
  ShieldCheck
} from "lucide-react";

const GlassCard = styled(Box)(({ theme }) => ({
  background: 'white',
  borderRadius: '24px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 6px rgba(0, 0, 0, 0.02)',
  border: '1px solid #F0F0F5',
  padding: '32px',
  marginBottom: '24px'
}));

const statuses = [
  "Open",
  "Pending",
  "On-Hold",
  "Resolved",
  "Waiting On Customer",
  "Reopen",
  "Waiting On agent",
];

const UserSIngleTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    getSingleTicket();
  }, []);

  const getSingleTicket = async () => {
    try {
      const res = await axiosInstance.get(`${apiEndPointsConfig.agentTicketById}/${id}`, {
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      });
      setTicket(res.data.data);
    } catch (error) {
      console.error("Error fetching ticket:", error);
    }
  };

  const chnageTicketStatus = async (values) => {
    try {
      const data = {
        ticketId: id,
        status: values,
      };
      await axiosInstance.post(
        `${apiEndPointsConfig.changeTicketStatus}`,
        data,
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      getSingleTicket(); // Refresh to get updated status
    } catch (error) {
      console.error("Error changing status:", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      status: ticket?.status || "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      chnageTicketStatus(values?.status);
    },
  });

  if (!ticket) return null;

  return (
    <Box sx={{ bgcolor: '#F8F9FC', minHeight: '100vh', display: 'flex' }}>
      <SideNavbar />
      <Box sx={{ flexGrow: 1, ml: { md: '280px' }, width: '100%', p: { xs: 2, md: 4 } }}>
        {/* Navigation & Actions Header */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ bgcolor: 'white', border: '1px solid #E5E7EB', borderRadius: '12px' }}
            >
              <ArrowLeft size={20} color="#4B5563" />
            </IconButton>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5" fontWeight={800} color="#111827">Ticket Details</Typography>
                <Typography variant="h6" fontWeight={600} color="#4F46E5" sx={{ fontSize: '14px', bgcolor: '#EEF2FF', px: 1.5, py: 0.5, borderRadius: '8px' }}>
                  #{id.slice(-6).toUpperCase()}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#6B7280' }}>
                  <Calendar size={14} />
                  <Typography variant="caption" fontWeight={600}>{new Date(ticket.createdAt).toLocaleDateString()}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#6B7280' }}>
                  <Clock size={14} />
                  <Typography variant="caption" fontWeight={600}>{new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>

          <Autocomplete
            disableClearable
            id="status"
            options={statuses}
            value={formik.values.status || ticket?.status || ""}
            onChange={(event, value) => {
              formik.setFieldValue("status", value);
              formik.submitForm();
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ticket Status"
                variant="outlined"
                sx={{
                  width: { xs: '100%', md: 220 },
                  "& .MuiOutlinedInput-root": {
                    bgcolor: 'white',
                    borderRadius: "12px",
                    "& fieldset": { borderColor: '#E5E7EB' },
                  },
                }}
              />
            )}
          />
        </Stack>

        <Grid container spacing={4}>
          <Grid item xs={12} lg={8}>
            <GlassCard>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ p: 1, bgcolor: '#EEF2FF', color: '#4F46E5', borderRadius: '10px' }}>
                  <ShieldCheck size={20} />
                </Box>
                <Typography variant="h6" fontWeight={800} color="#111827">{ticket.title}</Typography>
              </Stack>

              <Box sx={{ bgcolor: '#F9FAFB', p: 3, borderRadius: '16px', border: '1px solid #F3F4F6', mb: 4 }}>
                <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.7 }}>
                  {ticket.issue}
                </Typography>
              </Box>

              <Divider sx={{ my: 4, borderColor: '#F3F4F6' }} />

              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ p: 1, bgcolor: '#F0FDF4', color: '#16A34A', borderRadius: '10px' }}>
                    <MessageSquare size={20} />
                  </Box>
                  <Typography variant="h6" fontWeight={800} color="#111827">Discussion</Typography>
                </Stack>
                <TicketComment ticket={ticket} />
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <SingleTicketCard ticket={ticket} />

            <GlassCard sx={{ bgcolor: '#F5F3FF', border: '1px solid #DDD6FE' }}>
              <Typography variant="subtitle2" fontWeight={800} color="#5B21B6" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    justifyContent: 'space-between',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#6D28D9',
                    borderColor: '#DDD6FE',
                    bgcolor: 'white',
                    '&:hover': { bgcolor: '#FDFCFD', borderColor: '#7C3AED' }
                  }}
                >
                  Download Info
                  <ChevronRight size={18} />
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    justifyContent: 'space-between',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#6D28D9',
                    borderColor: '#DDD6FE',
                    bgcolor: 'white',
                    '&:hover': { bgcolor: '#FDFCFD', borderColor: '#7C3AED' }
                  }}
                >
                  Contact Client
                  <ChevronRight size={18} />
                </Button>
              </Stack>
            </GlassCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default UserSIngleTicket;
